/**
 * MobileWelcomeWizard — TODO-MDC-21.
 *
 * Bienvenida primera vez en mobile. Ofrece dos acciones de onboarding:
 *   1. Activar notificaciones push.
 *   2. Instalar como app (acceso directo al escritorio).
 *
 * Detección por plataforma:
 *   - Chrome/Edge Android → `beforeinstallprompt` (1-click install)
 *   - iOS Safari → instrucciones visuales paso a paso
 *   - Otros → instrucciones genéricas
 *
 * El wizard NO se muestra si:
 *   - No es mobile (matchMedia >= 768px)
 *   - La app ya está instalada (display-mode: standalone)
 *   - El usuario ya completó el onboarding (localStorage flag)
 *
 * Diseño: full-screen overlay sobre el contenido, modal-like pero sin
 * Drawer (el Drawer entra de costado y queda raro en mobile-first).
 * Botón "Después" siempre disponible — no forzar.
 */

import { useEffect, useState } from 'react';
import { Bell, Download, Share, Plus, X, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { BrandMark } from '@/components/common/BrandMark';
import { detectPlatform, type PlatformInfo } from '@/lib/platformDetect';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { toast } from 'sonner';

// localStorage = "ya completó" (no aparece nunca más).
// sessionStorage = "soft dismiss" (no molesto en esta sesión, pero
// vuelve a aparecer la próxima vez que abra la app si todavía no
// configuró nada).
//
// TODO-MDC-23: cambio crítico vs versión anterior. Antes, el simple
// "X" o "Después" marcaba 'done' permanente y el usuario quedaba
// sin forma de volver a abrir el wizard. Ahora el dismiss es soft;
// el 'done' permanente solo se marca tras un éxito real (push
// activado o promptInstall aceptado). Y siempre hay otra vía: el
// menú de usuario → Configuración móvil.
const DONE_KEY = 'manaxer.mobile-onboarding.done';
const SESSION_DISMISS_KEY = 'manaxer.mobile-onboarding.dismissed';

interface Props {
  empresaId?: string | null;
}

export function MobileWelcomeWizard({ empresaId }: Props) {
  const [shouldShow, setShouldShow] = useState(false);
  const [platform, setPlatform] = useState<PlatformInfo | null>(null);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const install = useInstallPrompt();
  const push = usePushSubscription(empresaId ?? null);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);

    if (!p.isMobile) return;
    if (p.isStandalone) return;
    if (typeof window === 'undefined') return;
    // 'done' permanente: el usuario ya completó configuración (push o install).
    if (window.localStorage.getItem(DONE_KEY) === 'done') return;
    // 'soft dismiss' por sesión: cerró el wizard ahora pero podría volver
    // a aparecer en próxima sesión.
    if (window.sessionStorage.getItem(SESSION_DISMISS_KEY) === 'true') return;

    setShouldShow(true);
  }, []);

  if (!shouldShow || !platform) return null;

  /** Cierra y marca como "done" definitivo (éxito real). */
  function closeDone() {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DONE_KEY, 'done');
      window.sessionStorage.removeItem(SESSION_DISMISS_KEY);
    }
    setShouldShow(false);
  }

  /** Cierra solo por esta sesión. Volverá a aparecer en próximo login. */
  function close() {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(SESSION_DISMISS_KEY, 'true');
    }
    setShouldShow(false);
  }

  async function onActivarNotif() {
    if (!platform?.supportsPush) {
      toast.info('Tu navegador no soporta notificaciones push.');
      return;
    }
    const ok = await push.enable();
    if (ok) {
      toast.success('Notificaciones activadas.');
    } else if (push.state.error) {
      toast.error(push.state.error);
    }
  }

  async function onActivarNotifWrap() {
    await onActivarNotif();
    // Si el push quedó activo, marcamos done definitivo.
    if (push.state.subscribed) closeDone();
  }

  async function onInstalar() {
    // Caso 1: Android Chrome/Edge → diálogo nativo.
    if (install.available) {
      const accepted = await install.promptInstall();
      if (accepted) {
        toast.success('App instalada. Buscala en tu pantalla de inicio.');
        closeDone();
      }
      return;
    }
    // Caso 2: iOS Safari u otros → instrucciones visuales.
    setShowInstallHelp(true);
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 backdrop-blur-sm md:hidden"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl">
        <button
          type="button"
          onClick={close}
          className="bg-neutral-soft absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full text-neutral-muted hover:text-neutral-ink"
          aria-label="Cerrar"
        >
          <X size={16} />
        </button>

        {!showInstallHelp ? (
          <>
            <div className="mb-4 flex flex-col items-center gap-3 pt-2">
              <BrandMark variant="isotipo" size={48} />
              <div className="text-center">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-blue">
                  Bienvenido
                </p>
                <h2 className="text-lg font-extrabold tracking-tight text-neutral-ink">
                  Configurá MANAXER en tu celular
                </h2>
                <p className="mt-1 text-xs text-neutral-muted">
                  Dos pasos rápidos para tener todo a mano.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {/* Notificaciones */}
              <div className="rounded-xl border border-blue-30 bg-blue-10/40 p-4">
                <div className="flex items-start gap-3">
                  <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-blue text-white">
                    <Bell size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-neutral-ink">
                      Activar notificaciones
                    </h3>
                    <p className="mt-0.5 text-[11px] text-neutral-muted">
                      Recibí avisos cuando termina un lote, cuando llega un cobro, o si
                      hay alertas pendientes — incluso con la app cerrada.
                    </p>
                  </div>
                </div>
                <Button
                  className="mt-3 w-full"
                  onClick={onActivarNotifWrap}
                  loading={push.state.busy}
                  disabled={
                    push.state.subscribed ||
                    push.state.permission === 'denied' ||
                    !platform.supportsPush
                  }
                >
                  {push.state.subscribed
                    ? '✓ Notificaciones activadas'
                    : push.state.permission === 'denied'
                      ? 'Bloqueadas — activá desde Ajustes del navegador'
                      : !platform.supportsPush
                        ? 'No soportado en este navegador'
                        : 'Activar notificaciones'}
                </Button>
              </div>

              {/* Instalar app */}
              <div className="border-orange-30 bg-orange-10/40 rounded-xl border p-4">
                <div className="flex items-start gap-3">
                  <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-orange text-white">
                    <Download size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-neutral-ink">
                      Instalar como app
                    </h3>
                    <p className="mt-0.5 text-[11px] text-neutral-muted">
                      Acceso directo en el escritorio del celu — abre en pantalla
                      completa, sin barras del navegador.
                    </p>
                  </div>
                </div>
                <Button className="mt-3 w-full" variant="accent" onClick={onInstalar}>
                  {install.available ? 'Instalar' : 'Cómo instalar'}
                </Button>
              </div>
            </div>

            <button
              type="button"
              onClick={close}
              className="mt-4 w-full rounded-md py-2 text-xs font-semibold text-neutral-muted hover:text-neutral-ink"
            >
              Después
            </button>
            <p className="text-neutral-faint mt-1 text-center text-[10px]">
              Si lo cerrás, podés volver a configurar todo desde tu perfil →
              <b> Configuración móvil</b>
            </p>
          </>
        ) : (
          <InstallHelp
            platform={platform}
            onBack={() => setShowInstallHelp(false)}
            onClose={close}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Instrucciones de instalación según plataforma/browser
// ─────────────────────────────────────────────────────────────────────────

function InstallHelp({
  platform,
  onBack,
  onClose,
}: {
  platform: PlatformInfo;
  onBack: () => void;
  onClose: () => void;
}) {
  let title = 'Cómo instalar MANAXER';
  let steps: Array<{ icon: React.ReactNode; text: React.ReactNode }> = [];

  if (platform.isIOS && platform.isSafari) {
    title = 'Instalar en iPhone / iPad';
    steps = [
      {
        icon: <Share size={16} className="text-blue" />,
        text: (
          <>
            Tocá el botón <b>Compartir</b> en la barra inferior de Safari (el cuadrado con
            la flecha hacia arriba).
          </>
        ),
      },
      {
        icon: <Plus size={16} className="text-blue" />,
        text: (
          <>
            En el menú que se abre, deslizá hasta encontrar{' '}
            <b>"Agregar a pantalla de inicio"</b> (ícono <b>+</b>) y tocalo.
          </>
        ),
      },
      {
        icon: <Bell size={16} className="text-blue" />,
        text: (
          <>
            Confirmá <b>Agregar</b> arriba a la derecha. El ícono de MANAXER aparece en tu
            escritorio.
          </>
        ),
      },
    ];
  } else if (platform.isIOS) {
    // iOS pero NO Safari (Chrome iOS, Firefox iOS) — no permite instalar.
    title = 'En iPhone usá Safari';
    steps = [
      {
        icon: <Share size={16} className="text-orange" />,
        text: (
          <>
            En iPhone, sólo Safari permite agregar apps a la pantalla de inicio. Abrí
            MANAXER en Safari (no en este navegador) y volvé a intentar.
          </>
        ),
      },
    ];
  } else if (platform.isAndroid) {
    title = 'Instalar en Android';
    steps = [
      {
        icon: <MoreHorizontal size={16} className="text-blue" />,
        text: (
          <>
            Tocá el botón de <b>menú</b> (los tres puntos verticales) arriba a la derecha
            del navegador.
          </>
        ),
      },
      {
        icon: <Download size={16} className="text-blue" />,
        text: (
          <>
            Tocá <b>"Instalar app"</b> o <b>"Agregar a pantalla principal"</b> (el texto
            varía según el navegador).
          </>
        ),
      },
      {
        icon: <Bell size={16} className="text-blue" />,
        text: <>Confirmá. El ícono aparece en tu pantalla de inicio.</>,
      },
    ];
  } else {
    // Desktop u otros — instrucciones genéricas
    steps = [
      {
        icon: <MoreHorizontal size={16} className="text-blue" />,
        text: (
          <>
            En la barra de direcciones del navegador buscá un ícono <b>"Instalar"</b>{' '}
            (varía según browser).
          </>
        ),
      },
    ];
  }

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-blue hover:underline"
      >
        ← Volver
      </button>
      <h2 className="text-lg font-extrabold tracking-tight text-neutral-ink">{title}</h2>
      <p className="mt-1 text-xs text-neutral-muted">
        Una vez instalada, la abrís desde el ícono y tenés notificaciones push aunque la
        pestaña esté cerrada.
      </p>

      <ol className="mt-4 space-y-3">
        {steps.map((s, i) => (
          <li
            key={i}
            className="flex items-start gap-3 rounded-xl border border-surface-line bg-white p-3"
          >
            <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-blue-10 font-bold text-blue">
              {i + 1}
            </span>
            <div className="flex-1 text-sm text-neutral-ink">{s.text}</div>
            <span className="flex-none">{s.icon}</span>
          </li>
        ))}
      </ol>

      <Button className="mt-5 w-full" variant="secondary" onClick={onClose}>
        Listo
      </Button>
    </div>
  );
}
